import React from "react";

import { Box, Grid, Typography, Button } from "@mui/material";

import Tooth from "./Tooth";
import Plaque from "./Plaque";

import { PERMANENT_ARCH_ROWS, PERMANENT_TO_DECIDUOUS, DECIDUOUS_TO_PERMANENT } from "../../../../config/constants/Constants";

// Helper to get the correct order of keys for each tooth
// Works for both permanent and deciduous teeth based on anatomical position
const getPlaqueKeys = (num, permanentPosition) => {
    // Use permanent position for key order (anatomical position)
    // If num is a deciduous tooth, map it to its permanent equivalent
    let n = Number(permanentPosition || num);

    if (n > 50 && DECIDUOUS_TO_PERMANENT[n]) {
        n = DECIDUOUS_TO_PERMANENT[n];
    }

    if ([18, 17, 16, 15, 14, 13, 12, 11].includes(n)) return ["db", "b", "mb", "dp", "p", "mp"];
    if ([21, 22, 23, 24, 25, 26, 27, 28].includes(n)) return ["mb", "b", "db", "mp", "p", "dp"];
    if ([48, 47, 46, 45, 44, 43, 42, 41].includes(n)) return ["dl", "l", "ml", "db", "b", "mb"];
    if ([31, 32, 33, 34, 35, 36, 37, 38].includes(n)) return ["ml", "l", "dl", "mb", "b", "db"];

    // fallback
    console.error("Invalid tooth number for plaque keys: " + num);
    return ["mb", "b", "db", "ml", "l", "dl"];
};

export default function ChartPlaque(props) {
    // state: chartingTable { [toothNumber: string]: { missing: bool, db: {plaque: bool, ...}, ... } }
    // setState: fn
    const { state, setState, field = "plaque", title = "Fast chart plaque", activeColor = "#1976d2", onInteracted } = props;

    // Helper to get effective tooth for a permanent position
    // Returns deciduous tooth number if it exists and permanent is missing
    const getEffectiveTooth = (permanentToothNum) => {
        const deciduousTooth = PERMANENT_TO_DECIDUOUS[permanentToothNum];

        if (deciduousTooth && state[deciduousTooth] && !state[deciduousTooth].missing && state[permanentToothNum]?.missing) {
            return deciduousTooth;
        }

        return permanentToothNum;
    };

    // Stable across renders so the memoized Plaque squares are not invalidated on every tap.
    const handleSiteClick = React.useCallback(
        (toothNum, key) => {
            setState((prev) => ({
                ...prev,
                [toothNum]: {
                    ...prev[toothNum],
                    [key]: {
                        ...prev[toothNum][key],
                        [field]: !prev[toothNum][key][field]
                    }
                }
            }));
            onInteracted?.();
        },
        [setState, field, onInteracted]
    );

    // Stable across renders; the missing check reads from the updater so `state` is not a dependency.
    const handleToothClick = React.useCallback(
        (toothNum) => {
            const keys = getPlaqueKeys(toothNum);
            const topKeys = keys.slice(0, 3);
            const bottomKeys = keys.slice(3, 6);
            let ignoredMissing = false;

            setState((prev) => {
                const tooth = prev[toothNum];
                if (tooth?.missing) {
                    ignoredMissing = true;
                    return prev; // Do not chart sites on a missing tooth.
                }

                // Determine current state
                const allSelected = keys.every((key) => tooth[key]?.[field] === true);
                const allCleared = keys.every((key) => !tooth[key]?.[field]);
                const onlyTopSelected = topKeys.every((key) => tooth[key]?.[field] === true) && bottomKeys.every((key) => !tooth[key]?.[field]);
                const onlyBottomSelected = bottomKeys.every((key) => tooth[key]?.[field] === true) && topKeys.every((key) => !tooth[key]?.[field]);

                const updatedTooth = { ...tooth };

                // Cycle: cleared -> all -> top only -> bottom only -> cleared
                if (allCleared) {
                    // Select all 6
                    keys.forEach((key) => {
                        updatedTooth[key] = { ...updatedTooth[key], [field]: true };
                    });
                } else if (allSelected) {
                    // Select only top row
                    topKeys.forEach((key) => {
                        updatedTooth[key] = { ...updatedTooth[key], [field]: true };
                    });
                    bottomKeys.forEach((key) => {
                        updatedTooth[key] = { ...updatedTooth[key], [field]: false };
                    });
                } else if (onlyTopSelected) {
                    // Select only bottom row
                    topKeys.forEach((key) => {
                        updatedTooth[key] = { ...updatedTooth[key], [field]: false };
                    });
                    bottomKeys.forEach((key) => {
                        updatedTooth[key] = { ...updatedTooth[key], [field]: true };
                    });
                } else if (onlyBottomSelected) {
                    // Clear all
                    keys.forEach((key) => {
                        updatedTooth[key] = { ...updatedTooth[key], [field]: false };
                    });
                } else {
                    // Mixed state - start cycle from all selected
                    keys.forEach((key) => {
                        updatedTooth[key] = { ...updatedTooth[key], [field]: true };
                    });
                }

                return {
                    ...prev,
                    [toothNum]: updatedTooth
                };
            });
            if (!ignoredMissing) {
                onInteracted?.();
            }
        },
        [setState, field, onInteracted]
    );

    return (
        <Box sx={{ width: "100%", mt: 1, mb: 1 }}>
            <Typography variant="h5" align="center" sx={{ mb: 2 }}>
                {title}
            </Typography>

            {/* Buccal upper */}
            <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1 }}>
                <Button variant="outlined" sx={{ fontSize: 15, px: 4, cursor: "default" }}>
                    Buccal
                </Button>
            </Box>

            <Grid
                container
                spacing={0.25}
                justifyContent="center"
                alignItems="center"
                sx={{ mb: 2, flexDirection: { xs: "column", sm: "row" }, flexWrap: { xs: "nowrap", sm: "wrap" } }}>
                {/* Upper right */}
                <Grid item sx={{ display: "flex", flexDirection: { xs: "row", sm: "row" }, flexWrap: "wrap", justifyContent: "center" }}>
                    {PERMANENT_ARCH_ROWS[0][0].map((permanentNum) => {
                        const effectiveNum = getEffectiveTooth(permanentNum);

                        return (
                            <Box key={permanentNum} sx={{ display: "flex", flexDirection: "column", alignItems: "center", mx: 0.5, mb: 1 }}>
                                {/* Top 3 boxes */}
                                <Box sx={{ display: "flex", mb: 0.5 }}>
                                    {getPlaqueKeys(effectiveNum, permanentNum)
                                        .slice(0, 3)
                                        .map((key) => (
                                            <Plaque
                                                key={key}
                                                name={key}
                                                num={effectiveNum}
                                                active={state[effectiveNum][key]?.[field]}
                                                handleToggle={handleSiteClick}
                                                activeColor={activeColor}
                                                alternatingColors
                                                disabled={!!state[effectiveNum]?.missing}
                                            />
                                        ))}
                                </Box>

                                <Tooth num={effectiveNum} missing={!!state[effectiveNum]?.missing} alternatingColors handleToggle={handleToothClick} />

                                <Box sx={{ display: "flex" }}>
                                    {getPlaqueKeys(effectiveNum, permanentNum)
                                        .slice(3, 6)
                                        .map((key) => (
                                            <Plaque
                                                key={key}
                                                name={key}
                                                num={effectiveNum}
                                                active={state[effectiveNum][key]?.[field]}
                                                handleToggle={handleSiteClick}
                                                activeColor={activeColor}
                                                alternatingColors
                                                disabled={!!state[effectiveNum]?.missing}
                                            />
                                        ))}
                                </Box>
                            </Box>
                        );
                    })}
                </Grid>

                {/* Gap */}
                <Box sx={{ width: { xs: 0, sm: 18 }, height: { xs: 16, sm: "auto" }, my: { xs: 1, sm: 0 } }} />

                {/* Upper left */}
                <Grid item sx={{ display: "flex", flexDirection: { xs: "row", sm: "row" }, flexWrap: "wrap", justifyContent: "center" }}>
                    {PERMANENT_ARCH_ROWS[0][1].map((permanentNum) => {
                        const effectiveNum = getEffectiveTooth(permanentNum);

                        return (
                            <Box key={permanentNum} sx={{ display: "flex", flexDirection: "column", alignItems: "center", mx: 0.5, mb: 1 }}>
                                <Box sx={{ display: "flex", mb: 0.5 }}>
                                    {getPlaqueKeys(effectiveNum, permanentNum)
                                        .slice(0, 3)
                                        .map((key) => (
                                            <Plaque
                                                key={key}
                                                name={key}
                                                num={effectiveNum}
                                                active={state[effectiveNum][key]?.[field]}
                                                handleToggle={handleSiteClick}
                                                activeColor={activeColor}
                                                alternatingColors
                                                disabled={!!state[effectiveNum]?.missing}
                                            />
                                        ))}
                                </Box>

                                <Tooth num={effectiveNum} missing={!!state[effectiveNum]?.missing} alternatingColors handleToggle={handleToothClick} />

                                <Box sx={{ display: "flex" }}>
                                    {getPlaqueKeys(effectiveNum, permanentNum)
                                        .slice(3, 6)
                                        .map((key) => (
                                            <Plaque
                                                key={key}
                                                name={key}
                                                num={effectiveNum}
                                                active={state[effectiveNum][key]?.[field]}
                                                handleToggle={handleSiteClick}
                                                activeColor={activeColor}
                                                alternatingColors
                                                disabled={!!state[effectiveNum]?.missing}
                                            />
                                        ))}
                                </Box>
                            </Box>
                        );
                    })}
                </Grid>
            </Grid>

            {/* Lingual lower */}
            <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1 }}>
                <Button variant="outlined" sx={{ fontSize: 15, px: 4, cursor: "default" }}>
                    Lingual
                </Button>
            </Box>

            <Grid
                container
                spacing={0.25}
                justifyContent="center"
                alignItems="center"
                sx={{ mb: 2, flexDirection: { xs: "column", sm: "row" }, flexWrap: { xs: "nowrap", sm: "wrap" } }}>
                {/* Lower right */}
                <Grid item sx={{ display: "flex", flexDirection: { xs: "row", sm: "row" }, flexWrap: "wrap", justifyContent: "center" }}>
                    {PERMANENT_ARCH_ROWS[1][0].map((permanentNum) => {
                        const effectiveNum = getEffectiveTooth(permanentNum);

                        return (
                            <Box key={permanentNum} sx={{ display: "flex", flexDirection: "column", alignItems: "center", mx: 0.5, mb: 1 }}>
                                <Box sx={{ display: "flex", mb: 0.5 }}>
                                    {getPlaqueKeys(effectiveNum, permanentNum)
                                        .slice(0, 3)
                                        .map((key) => (
                                            <Plaque
                                                key={key}
                                                name={key}
                                                num={effectiveNum}
                                                active={state[effectiveNum][key]?.[field]}
                                                handleToggle={handleSiteClick}
                                                activeColor={activeColor}
                                                alternatingColors
                                                disabled={!!state[effectiveNum]?.missing}
                                            />
                                        ))}
                                </Box>

                                <Tooth num={effectiveNum} missing={!!state[effectiveNum]?.missing} alternatingColors handleToggle={handleToothClick} />

                                <Box sx={{ display: "flex" }}>
                                    {getPlaqueKeys(effectiveNum, permanentNum)
                                        .slice(3, 6)
                                        .map((key) => (
                                            <Plaque
                                                key={key}
                                                name={key}
                                                num={effectiveNum}
                                                active={state[effectiveNum][key]?.[field]}
                                                handleToggle={handleSiteClick}
                                                activeColor={activeColor}
                                                alternatingColors
                                                disabled={!!state[effectiveNum]?.missing}
                                            />
                                        ))}
                                </Box>
                            </Box>
                        );
                    })}
                </Grid>

                {/* Gap */}
                <Box sx={{ width: { xs: 0, sm: 18 }, height: { xs: 16, sm: "auto" }, my: { xs: 1, sm: 0 } }} />

                {/* Lower left */}
                <Grid item sx={{ display: "flex", flexDirection: { xs: "row", sm: "row" }, flexWrap: "wrap", justifyContent: "center" }}>
                    {PERMANENT_ARCH_ROWS[1][1].map((permanentNum) => {
                        const effectiveNum = getEffectiveTooth(permanentNum);

                        return (
                            <Box key={permanentNum} sx={{ display: "flex", flexDirection: "column", alignItems: "center", mx: 0.5, mb: 1 }}>
                                <Box sx={{ display: "flex", mb: 0.5 }}>
                                    {getPlaqueKeys(effectiveNum, permanentNum)
                                        .slice(0, 3)
                                        .map((key) => (
                                            <Plaque
                                                key={key}
                                                name={key}
                                                num={effectiveNum}
                                                active={state[effectiveNum][key]?.[field]}
                                                handleToggle={handleSiteClick}
                                                activeColor={activeColor}
                                                alternatingColors
                                                disabled={!!state[effectiveNum]?.missing}
                                            />
                                        ))}
                                </Box>

                                <Tooth num={effectiveNum} missing={!!state[effectiveNum]?.missing} alternatingColors handleToggle={handleToothClick} />

                                <Box sx={{ display: "flex" }}>
                                    {getPlaqueKeys(effectiveNum, permanentNum)
                                        .slice(3, 6)
                                        .map((key) => (
                                            <Plaque
                                                key={key}
                                                name={key}
                                                num={effectiveNum}
                                                active={state[effectiveNum][key]?.[field]}
                                                handleToggle={handleSiteClick}
                                                activeColor={activeColor}
                                                alternatingColors
                                                disabled={!!state[effectiveNum]?.missing}
                                            />
                                        ))}
                                </Box>
                            </Box>
                        );
                    })}
                </Grid>
            </Grid>

            {/* Buccal lower */}
            <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1 }}>
                <Button variant="outlined" sx={{ fontSize: 15, px: 4, cursor: "default" }}>
                    Buccal
                </Button>
            </Box>
        </Box>
    );
}
